import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { dashboardApi, presupuestosApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { 
  FileText, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Plus,
  ArrowUpRight,
  Eye
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function DashboardPage() {
  const { empresaActual } = useAuth();
  const navigate = useNavigate();
  const [metricas, setMetricas] = useState(null);
  const [presupuestosRecientes, setPresupuestosRecientes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!empresaActual?.id) return;
      
      try {
        const [metricasRes, presupuestosRes] = await Promise.all([
          dashboardApi.getMetricas(empresaActual.id),
          presupuestosApi.getAll(empresaActual.id)
        ]);
        setMetricas(metricasRes.data);
        setPresupuestosRecientes(presupuestosRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error loading dashboard:', error);
        toast.error('Error al cargar el dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [empresaActual?.id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  const metricCards = [
    {
      title: 'Enviados este mes',
      value: metricas?.total_enviados || 0,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Valor cotizado',
      value: formatCurrency(metricas?.valor_total || 0),
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    },
    {
      title: 'Tasa de aprobación',
      value: `${metricas?.tasa_aprobacion || 0}%`,
      icon: CheckCircle,
      color: 'text-violet-600',
      bgColor: 'bg-violet-100'
    },
    {
      title: 'Pendientes',
      value: metricas?.pendientes || 0,
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 lg:space-y-8 animate-fadeIn" data-testid="dashboard-page">
        {/* Header - Hidden on mobile (info is in header) */}
        <div className="hidden lg:flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-slate-500 mt-1">
              Bienvenido de vuelta. Aquí tienes un resumen de tu actividad.
            </p>
          </div>
          <Button 
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => navigate('/presupuestos/nuevo')}
            data-testid="dashboard-new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Presupuesto
          </Button>
        </div>

        {/* Mobile Welcome */}
        <div className="lg:hidden">
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Hola, bienvenido
          </h1>
          <p className="text-slate-500 text-sm">
            Resumen de tu actividad
          </p>
        </div>

        {/* Metrics Grid - 2 cols on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
          {metricCards.map((metric, index) => (
            <Card key={index} className="metric-card" data-testid={`metric-${metric.title.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs lg:text-sm text-slate-500 truncate">{metric.title}</p>
                    <p className="metric-value mt-1 truncate">{metric.value}</p>
                  </div>
                  <div className={`w-8 h-8 lg:w-10 lg:h-10 ${metric.bgColor} rounded-lg flex items-center justify-center shrink-0 ml-2`}>
                    <metric.icon className={`w-4 h-4 lg:w-5 lg:h-5 ${metric.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Secondary Metrics - Stack on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-6">
          <Card>
            <CardContent className="p-4 lg:p-6 flex items-center gap-3 lg:gap-4">
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-xl lg:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {metricas?.aprobados_semana || 0}
                </p>
                <p className="text-xs lg:text-sm text-slate-500">Aprobados esta semana</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 lg:p-6 flex items-center gap-3 lg:gap-4">
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-amber-100 rounded-lg flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 lg:w-6 lg:h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-xl lg:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {metricas?.por_vencer || 0}
                </p>
                <p className="text-xs lg:text-sm text-slate-500">Por vencer (7 días)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart and Recent - Stack on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Chart */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2 lg:pb-4">
              <CardTitle className="text-base lg:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Actividad (6 meses)
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="h-48 lg:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={metricas?.actividad || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="mes" stroke="#64748b" fontSize={11} />
                    <YAxis stroke="#64748b" fontSize={11} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        fontSize: '12px'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="enviados" name="Enviados" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="aprobados" name="Aprobados" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Recent Quotes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 lg:pb-4">
              <CardTitle className="text-base lg:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Recientes
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-blue-600 text-xs lg:text-sm h-8"
                onClick={() => navigate('/presupuestos')}
              >
                Ver todos
                <ArrowUpRight className="w-3 h-3 lg:w-4 lg:h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent className="pb-4">
              {presupuestosRecientes.length === 0 ? (
                <div className="text-center py-6 lg:py-8 text-slate-500">
                  <FileText className="w-10 h-10 lg:w-12 lg:h-12 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm">No hay presupuestos aún</p>
                </div>
              ) : (
                <div className="space-y-2 lg:space-y-3">
                  {presupuestosRecientes.map((p) => (
                    <div 
                      key={p.id} 
                      className="flex items-center justify-between p-2 lg:p-3 rounded-lg hover:bg-slate-50 active:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => navigate(`/presupuestos/${p.id}`)}
                      data-testid={`recent-quote-${p.numero}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">{p.numero}</p>
                        <p className="text-xs text-slate-500 truncate">{p.cliente?.nombre || 'Sin cliente'}</p>
                      </div>
                      <div className="text-right ml-3">
                        <p className="font-medium text-slate-900 text-sm">{formatCurrency(p.total)}</p>
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] lg:text-xs font-medium rounded border ${getStatusColor(p.estado)}`}>
                          {getStatusLabel(p.estado)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
